// The in-memory "server database" that the MSW handlers read and write.
// State mirrors into localStorage so edits survive a reload, which is what the
// real Go server + Postgres would give you.

import {Block, BlockPatch, createPatchesFromBlocks} from '../types/block'
import {Board, BoardPatch} from '../types/board'
import {CategoryBoards} from '../types/category'
import {IUser} from '../types/user'
import {Constants} from '../common/constants'
import {buildSeed, SeedData} from './seed'

export type Database = SeedData

let db: Database | null = null

function persist(): void {
    try {
        window.localStorage.setItem(Constants.localStorageKeys.mockDatabase, JSON.stringify(db))
    } catch (e) {
        // Private mode / quota: the session still works, it just won't persist.
        console.warn('[mock-db] unable to persist state:', e)
    }
}

function isDatabase(value: any): value is Database {
    return Boolean(value) && Array.isArray(value.boards) && Array.isArray(value.blocks) && Array.isArray(value.teams)
}

function load(): Database {
    const raw = window.localStorage.getItem(Constants.localStorageKeys.mockDatabase)
    if (raw) {
        try {
            const parsed = JSON.parse(raw)
            if (isDatabase(parsed)) {
                return parsed
            }
        } catch {
            // corrupt payload: reseed below
        }
    }
    const seeded = buildSeed()
    db = seeded
    persist()
    return seeded
}

export function database(): Database {
    if (!db) {
        db = load()
    }
    return db
}

export function resetDatabase(): Database {
    window.localStorage.removeItem(Constants.localStorageKeys.mockDatabase)
    db = buildSeed()
    persist()
    return db
}

// ------------------------------------------------------------- boards -----

export function boardsOfTeam(teamId: string, templates = false): Board[] {
    return database().boards.filter((b) => b.teamId === teamId && b.deleteAt === 0 && b.isTemplate === templates)
}

export function findBoard(boardId: string): Board | undefined {
    return database().boards.find((b) => b.id === boardId && b.deleteAt === 0)
}

export function upsertBoard(board: Board): Board {
    const list = database().boards
    const index = list.findIndex((b) => b.id === board.id)
    if (index >= 0) {
        list[index] = board
    } else {
        list.push(board)
    }
    persist()
    return board
}

export function patchBoard(boardId: string, patch: Partial<BoardPatch> & {deleteAt?: number, modifiedBy?: string}): Board | undefined {
    const board = database().boards.find((b) => b.id === boardId)
    if (!board) {
        return undefined
    }

    const next: Board = {
        ...board,
        properties: {...board.properties},
        cardProperties: board.cardProperties.map((cp) => ({...cp, options: cp.options.map((o) => ({...o}))})),
    }

    if (patch.title !== undefined) {
        next.title = patch.title
    }
    if (patch.description !== undefined) {
        next.description = patch.description
    }
    if (patch.icon !== undefined) {
        next.icon = patch.icon
    }
    if (patch.type !== undefined) {
        next.type = patch.type
    }
    if (patch.minimumRole !== undefined) {
        next.minimumRole = patch.minimumRole
    }
    if (patch.showDescription !== undefined) {
        next.showDescription = patch.showDescription
    }
    if (patch.deleteAt !== undefined) {
        next.deleteAt = patch.deleteAt
    }
    if (patch.updatedProperties) {
        next.properties = {...next.properties, ...patch.updatedProperties}
    }
    if (patch.deletedProperties) {
        patch.deletedProperties.forEach((key) => delete next.properties[key])
    }
    if (patch.updatedCardProperties) {
        patch.updatedCardProperties.forEach((p) => {
            const i = next.cardProperties.findIndex((cp) => cp.id === p.id)
            if (i >= 0) {
                next.cardProperties[i] = p
            } else {
                next.cardProperties.push(p)
            }
        })
    }
    if (patch.deletedCardProperties) {
        const deleted = patch.deletedCardProperties
        next.cardProperties = next.cardProperties.filter((cp) => !deleted.includes(cp.id))
    }

    next.modifiedBy = patch.modifiedBy || next.modifiedBy
    next.updateAt = Date.now()
    return upsertBoard(next)
}

export function deleteBoard(boardId: string): boolean {
    return patchBoard(boardId, {deleteAt: Date.now()}) !== undefined
}

// ------------------------------------------------------------- blocks -----

export type BlockQuery = {
    parentId?: string
    type?: string
    boardId?: string
    blockId?: string
    includeDeleted?: boolean
}

export function queryBlocks(query: BlockQuery): Block[] {
    return database().blocks.filter((b) => {
        if (query.blockId) {
            return b.id === query.blockId
        }
        if (!query.includeDeleted && b.deleteAt !== 0) {
            return false
        }
        if (query.boardId && b.boardId !== query.boardId) {
            return false
        }
        if (query.parentId && b.parentId !== query.parentId) {
            return false
        }
        if (query.type && b.type !== query.type) {
            return false
        }
        return true
    })
}

export function findBlock(blockId: string): Block | undefined {
    return database().blocks.find((b) => b.id === blockId)
}

export function upsertBlock(block: Block): Block {
    const list = database().blocks
    const index = list.findIndex((b) => b.id === block.id)
    if (index >= 0) {
        list[index] = block
    } else {
        list.push(block)
    }
    persist()
    return block
}

export function insertBlock(block: Block): Block {
    const existing = findBlock(block.id)
    if (existing) {
        return upsertBlock({...existing, ...block})
    }
    return upsertBlock(block)
}

export function patchBlock(blockId: string, patch: BlockPatch): Block | undefined {
    const block = findBlock(blockId)
    if (!block) {
        return undefined
    }

    const next: Block = {...block, fields: {...block.fields}}

    if (patch.title !== undefined) {
        next.title = patch.title
    }
    if (patch.type !== undefined) {
        next.type = patch.type
    }
    if (patch.parentId !== undefined) {
        next.parentId = patch.parentId
    }
    if (patch.deleteAt !== undefined) {
        next.deleteAt = patch.deleteAt
    }
    if (patch.updatedFields) {
        Object.keys(patch.updatedFields).forEach((key) => {
            next.fields[key] = (patch.updatedFields as Record<string, unknown>)[key]
        })
    }
    if (patch.deletedFields) {
        patch.deletedFields.forEach((key) => delete next.fields[key])
    }
    next.updateAt = Date.now()

    const board = database().boards.find((b) => b.id === next.boardId)
    if (board) {
        board.updateAt = next.updateAt
    }

    return upsertBlock(next)
}

export function deleteBlock(blockId: string): boolean {
    return patchBlock(blockId, {deleteAt: Date.now()}) !== undefined
}

export function undeleteBlock(blockId: string): boolean {
    return patchBlock(blockId, {deleteAt: 0}) !== undefined
}

/** Exposed for parity checks against the server's patch/undo pair. */
export const blockPatchPair = createPatchesFromBlocks

// ----------------------------------------------------------- users --------

export function allUsers(): IUser[] {
    return database().users
}

export function findUser(idOrUsername: string): IUser | undefined {
    return allUsers().find((u) => u.id === idOrUsername || u.username === idOrUsername)
}

export function addUser(user: IUser, password: string): IUser {
    database().users.push(user)
    database().credentials.push({userId: user.id, username: user.username, password})
    persist()
    return user
}

export function checkCredentials(username: string, password: string): IUser | undefined {
    const cred = database().credentials.find((c) => c.username === username && c.password === password)
    return cred ? findUser(cred.userId) : undefined
}

/**
 * The real server issues opaque session tokens; here the user id is carried in
 * the token itself (`mock.<userId>.<rand>`) so no session table is needed.
 */
export function createSession(userId: string): string {
    return `mock.${userId}.${Math.random().toString(36).slice(2, 10)}`
}

export function userForToken(token: string): string | undefined {
    const match = /^mock\.([^.]+)\./.exec(token || '')
    return match ? match[1] : undefined
}

// -------------------------------------------------------- categories ------

export function categoriesOfTeam(teamId: string): CategoryBoards[] {
    return database().categories.
        filter((c) => c.teamID === teamId && c.deleteAt === 0).
        slice().
        sort((a, b) => a.sortOrder - b.sortOrder)
}

export function upsertCategory(category: CategoryBoards): CategoryBoards {
    const list = database().categories
    const index = list.findIndex((c) => c.id === category.id)
    if (index >= 0) {
        list[index] = category
    } else {
        list.push(category)
    }
    persist()
    return category
}

export function teamIds(): string[] {
    return database().teams.map((t) => t.id)
}
